import { apiSlice } from './apiSlice';

export interface LakehouseLayer {
  lakehouse_layer_id: string;
  lakehouse_id: string;
  storage_path: string | null;
  medallion_layer: string;
  status: number;
  created_id: string;
  created_at: string;
  updated_id: string | null;
  updated_at: string | null;
  file_count: number;
  table_count: number;
}

export interface Lakehouse {
  lakehouse_id: string;
  customer_id: string;
  client_id: string;
  name: string;
  description: string;
  code: string;
  created_at: string;
  status: number;
  lakehouse_layers?: LakehouseLayer[];
}

export interface DataFile {
  data_file_id: string;
  name: string;
  file_path: string;
  file_type: string | null;
  size_bytes: number | null;
  lakehouse_id: string;
  lakehouse_layer_id: string;
  client_id: string;
  customer_id: string;
  uploaded_at: string;
  processed_at: string | null;
  is_processed: boolean;
  file_metadata: string | null;
  status: number;
  created_id: string;
  created_at: string;
  updated_id: string | null;
  updated_at: string | null;
}

export interface DataTable {
  data_table_id: string;
  name: string;
  description: string;
  schema_definition: string | null;
  table_path: string;
  row_count: number;
  size_bytes: number;
  lakehouse_id: string;
  lakehouse_layer_id: string;
  client_id: string;
  customer_id: string;
  created_at: string;
  layer: string;
  status: number;
  created_id: string;
  updated_id: string | null;
  updated_at: string | null;
}

export interface GetLakehousesRequest {
  customerId: string;
  clientId: string;
  includeLayers?: boolean;
}

export const lakehouseApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLakehousesByCustomerAndClient: builder.query<Lakehouse[], GetLakehousesRequest>({
      query: ({ customerId, clientId, includeLayers }) => {
        let url = `/api/v1/lakehouses?customer_id=${customerId}&client_id=${clientId}`;
        if (includeLayers) {
          url += '&layers=true';
        }
        return {
          url,
          method: 'GET',
        };
      },
      transformResponse: (response: { lakehouses: Lakehouse[] }) => {
        const sortedLakehouses = [...(response.lakehouses || [])].sort((a, b) => a.name.localeCompare(b.name));

        const layerOrder = ['Bronze', 'Silver', 'Gold'];

        sortedLakehouses.forEach((lakehouse) => {
          if (lakehouse.lakehouse_layers) {
            lakehouse.lakehouse_layers.sort((a, b) => {
              const aIndex = layerOrder.indexOf(a.medallion_layer);
              const bIndex = layerOrder.indexOf(b.medallion_layer);
              return aIndex - bIndex;
            });
          }
        });

        return sortedLakehouses;
      },
      providesTags: (result, error, { customerId, clientId }) => [
        { type: 'Lakehouse', id: `${customerId}-${clientId}` },
      ],
    }),
    createLakehouse: builder.mutation<Lakehouse, Partial<Lakehouse>>({
      query: (body) => ({
        url: '/api/v1/lakehouses',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { customer_id, client_id }) => [
        { type: 'Lakehouse', id: `${customer_id}-${client_id}` },
        'DataPlatformDashboard',
      ],
    }),
    updateLakehouse: builder.mutation<Lakehouse, Partial<Lakehouse> & { lakehouse_id: string }>({
      query: ({ lakehouse_id, ...body }) => ({
        url: `/api/v1/lakehouses/${lakehouse_id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { customer_id, client_id }) => [
        { type: 'Lakehouse', id: `${customer_id}-${client_id}` },
        'DataPlatformDashboard',
        { type: 'GeneratedOntologies', id: 'LIST' },
      ],
    }),
    getLakehouse: builder.query<Lakehouse, { lakehouseId: string; includeLayers?: boolean }>({
      query: ({ lakehouseId, includeLayers }) => {
        let url = `/api/v1/lakehouses/${lakehouseId}`;
        if (includeLayers) {
          url += '?layers=true';
        }
        return {
          url,
          method: 'GET',
        };
      },
      transformResponse: (response: { lakehouse: Lakehouse }) => {
        const layerOrder = ['Bronze', 'Silver', 'Gold'];
        if (response.lakehouse && response.lakehouse.lakehouse_layers) {
          response.lakehouse.lakehouse_layers.sort((a, b) => {
            const aIndex = layerOrder.indexOf(a.medallion_layer);
            const bIndex = layerOrder.indexOf(b.medallion_layer);
            return aIndex - bIndex;
          });
        }
        return response.lakehouse;
      },
      providesTags: (result, error, { lakehouseId }) => [{ type: 'Lakehouse', id: lakehouseId }],
    }),

    validateDataFiles: builder.mutation({
      query: (data) => ({
        url: `/api/v1/lakehouses/${data.lakehouse_layer_id}/validate-upload`,
        method: 'POST',
        body: data,
      }),
    }),

    getDataFiles: builder.query<DataFile[], string>({
      query: (lakehouseLayerId) => `/api/v1/lakehouses/${lakehouseLayerId}/files`,
      providesTags: (result, error, lakehouseLayerId) => [{ type: 'DataFile', id: lakehouseLayerId }],
      transformResponse: (response: { data_files: DataFile[] }) => {
        if (response && response.data_files) {
          return [...response.data_files].sort((a, b) => a.name.localeCompare(b.name));
        }
        return [];
      },
    }),

    getDataTables: builder.query<DataTable[], string>({
      query: (lakehouseLayerId) => `/api/v1/lakehouses/${lakehouseLayerId}/tables`,
      providesTags: (result, error, lakehouseLayerId) => [{ type: 'DataTable', id: lakehouseLayerId }],
      transformResponse: (response: { data_tables: DataTable[] }) => {
        if (response && response.data_tables) {
          return [...response.data_tables].sort((a, b) => a.name.localeCompare(b.name));
        }
        return [];
      },
    }),

    uploadDataFile: builder.mutation<any, { lakehouseLayerId: string; file: File }>({
      query: ({ lakehouseLayerId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/api/v1/lakehouses/layers/${lakehouseLayerId}/upload-file`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (result, error, { lakehouseLayerId }) => [
        { type: 'DataFile', id: lakehouseLayerId },
        'DataPlatformDashboard',
      ],
    }),
  }),
});

export const {
  useGetLakehousesByCustomerAndClientQuery,
  useCreateLakehouseMutation,
  useUpdateLakehouseMutation,
  useGetLakehouseQuery,
  useValidateDataFilesMutation,
  useGetDataFilesQuery,
  useGetDataTablesQuery,
  useUploadDataFileMutation,
} = lakehouseApiSlice;
